import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaChartLine, FaBars, FaTimes, FaWallet, FaChartPie, FaPlusCircle, FaTachometerAlt, FaSignOutAlt, FaIdCard, FaMoneyBillWave, FaUsers, FaGift } from 'react-icons/fa';
import { logout } from '../../store/slices/authSlice';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const investorLinks = [
    { name: 'Dashboard', path: '/investor/dashboard', icon: <FaTachometerAlt className="mr-1" /> },
    { name: 'Invest Now', path: '/investor/plans', icon: <FaPlusCircle className="mr-1" /> },
    { name: 'Investments', path: '/investor/my-investments', icon: <FaChartPie className="mr-1" /> },
    { name: 'Wallet', path: '/investor/wallet', icon: <FaWallet className="mr-1" /> },
    { name: 'Profit', path: '/investor/profit-tracker', icon: <FaChartLine className="mr-1" /> },
    { name: 'Withdraw', path: '/investor/withdraw', icon: <FaMoneyBillWave className="mr-1" /> },
    { name: 'KYC', path: '/investor/kyc', icon: <FaIdCard className="mr-1" /> },
    { name: 'My Team', path: '/investor/my-team', icon: <FaUsers className="mr-1" /> },
    { name: 'Bonus', path: '/investor/bonus', icon: <FaGift className="mr-1" /> },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm shadow-sm'}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center">
              <FaChartLine className="text-white text-xl" />
            </div>
            <div className="flex flex-col">
              <span className="text-base md:text-lg font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">Assets Dairy</span>
              <span className="text-[10px] md:text-xs bg-yellow-500 text-gray-900 px-2 py-0.5 rounded-full font-semibold w-fit">Investor</span>
            </div>
          </Link>

          {/* Desktop Menu - Hidden on mobile */}
          {isAuthenticated && user?.role !== 'admin' && (
            <div className="hidden md:flex space-x-2">
              {investorLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center px-3 py-2 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-lg transition text-sm font-medium"
                >
                  {link.icon}
                  {link.name}
                </Link>
              ))}
            </div>
          )}

          {isAuthenticated && user?.role === 'admin' && (
            <div className="hidden md:flex space-x-2">
              <Link to="/admin" className="px-3 py-2 text-sm text-gray-700 hover:text-green-600 rounded-lg">Dashboard</Link>
              <Link to="/admin/users" className="px-3 py-2 text-sm text-gray-700 hover:text-green-600 rounded-lg">Users</Link>
              <Link to="/admin/investments" className="px-3 py-2 text-sm text-gray-700 hover:text-green-600 rounded-lg">Investments</Link>
              <Link to="/admin/kyc" className="px-3 py-2 text-sm text-gray-700 hover:text-green-600 rounded-lg">KYC</Link>
              <Link to="/admin/withdrawals" className="px-3 py-2 text-sm text-gray-700 hover:text-green-600 rounded-lg">Withdrawals</Link>
              <Link to="/admin/referrals" className="px-3 py-2 text-sm text-gray-700 hover:text-green-600 rounded-lg">Referrals</Link>
              <Link to="/admin/bonuses" className="px-3 py-2 text-sm text-gray-700 hover:text-green-600 rounded-lg">Bonuses</Link>
            </div>
          )}

          {/* Right Section - Always visible */}
          <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              <div className="relative group">
                <button className="flex items-center space-x-2 focus:outline-none bg-gray-100 hover:bg-gray-200 rounded-full pl-2 pr-3 py-1 transition">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-gray-700 font-medium text-sm hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                </button>
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border overflow-hidden">
                  <div className="p-3 border-b bg-gray-50">
                    <p className="font-semibold text-gray-800 text-sm">{user?.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    {user?.userId && <p className="text-xs text-gray-500 mt-1">ID: {user.userId}</p>}
                    {user?.referralCode && <p className="text-xs text-green-600 mt-1">Code: {user.referralCode}</p>}
                  </div>
                  {user?.role === 'admin' ? (
                    <>
                      <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Dashboard</Link>
                      <Link to="/admin/users" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Users</Link>
                      <Link to="/admin/investments" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Investments</Link>
                      <Link to="/admin/kyc" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">KYC</Link>
                      <Link to="/admin/withdrawals" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Withdrawals</Link>
                      <Link to="/admin/referrals" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Referrals</Link>
                      <Link to="/admin/bonuses" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Bonuses</Link>
                    </>
                  ) : (
                    <>
                      <Link to="/investor/dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Dashboard</Link>
                      <Link to="/investor/plans" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Invest Now</Link>
                      <Link to="/investor/my-investments" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Investments</Link>
                      <Link to="/investor/wallet" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Wallet</Link>
                      <Link to="/investor/profit-tracker" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Profit</Link>
                      <Link to="/investor/withdraw" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Withdraw</Link>
                      <Link to="/investor/kyc" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">KYC</Link>
                      <Link to="/investor/my-team" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">My Team</Link>
                      <Link to="/investor/bonus" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50">Bonus</Link>
                    </>
                  )}
                  <button onClick={handleLogout} className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition border-t">
                    <FaSignOutAlt /> Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="text-gray-600 hover:text-green-600 transition font-medium text-sm">Login</Link>
                <Link to="/register" className="bg-green-600 text-white px-4 py-1.5 rounded-lg hover:bg-green-700 transition text-sm">Sign Up</Link>
              </div>
            )}

            {/* Mobile Menu Button - Only visible on mobile */}
            <button className="md:hidden p-2 rounded-lg hover:bg-gray-100" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t space-y-2">
            {!isAuthenticated ? (
              <div className="flex flex-col space-y-2">
                <Link to="/" className="block py-2 text-gray-700 border-b" onClick={() => setIsOpen(false)}>Home</Link>
                <Link to="/about" className="block py-2 text-gray-700 border-b" onClick={() => setIsOpen(false)}>About</Link>
                <Link to="/contact" className="block py-2 text-gray-700 border-b" onClick={() => setIsOpen(false)}>Contact</Link>
                <div className="flex gap-2 mt-2">
                  <Link to="/login" className="flex-1 bg-gray-100 text-green-600 text-center py-2 rounded-lg" onClick={() => setIsOpen(false)}>Login</Link>
                  <Link to="/register" className="flex-1 bg-green-600 text-white text-center py-2 rounded-lg" onClick={() => setIsOpen(false)}>Register</Link>
                </div>
              </div>
            ) : user?.role === 'admin' ? (
              <>
                <Link to="/admin" className="block py-2 text-gray-700 border-b" onClick={() => setIsOpen(false)}>Dashboard</Link>
                <Link to="/admin/users" className="block py-2 text-gray-700 border-b" onClick={() => setIsOpen(false)}>Users</Link>
                <Link to="/admin/investments" className="block py-2 text-gray-700 border-b" onClick={() => setIsOpen(false)}>Investments</Link>
                <Link to="/admin/kyc" className="block py-2 text-gray-700 border-b" onClick={() => setIsOpen(false)}>KYC</Link>
                <Link to="/admin/withdrawals" className="block py-2 text-gray-700 border-b" onClick={() => setIsOpen(false)}>Withdrawals</Link>
                <Link to="/admin/bonuses" className="block py-2 text-gray-700 border-b" onClick={() => setIsOpen(false)}>Bonuses</Link>
                <button onClick={() => { handleLogout(); setIsOpen(false); }} className="block w-full text-left py-2 text-red-600 mt-2">Logout</button>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {investorLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-lg transition text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.icon}
                    {link.name}
                  </Link>
                ))}
                <button onClick={() => { handleLogout(); setIsOpen(false); }} className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition text-sm">
                  <FaSignOutAlt /> Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;